<template>
  <div>
    <div class="dropdown dropdown__background dropdown__no-lines">
      <button
        id="dropdownHeaderCompanies"
        class="button button__lines-grey d-flex align-items-center"
        data-bs-toggle="dropdown"
      >
        <span class="mx-auto" style="width: 23px">
          <span style="height: 23px; border-radius: 50%">
            <i class="fas fa-building" />
          </span>
        </span>
        <span class="d-none d-md-block ms-2">{{
          selectedClient ? selectedClient.name : 'Unknown name'
        }}</span>
      </button>
      <ul
        aria-labelledby="dropdownHeaderCompanies"
        class="dropdown-menu dropdown-menu-end"
      >
        <!-- Temporarily hidden - Team functionality -->
        <!-- <li>
          <nuxt-link class="dropdown-item" to="/settings/team">
            {{ t('company.top_menu.team') }}
          </nuxt-link>
        </li> -->
        <li>
          <nuxt-link class="dropdown-item" to="/settings">
            {{ t('company.top_menu.settings') }}
          </nuxt-link>
        </li>
        <template v-if="clients.length > 1">
          <li>
            <hr class="dropdown-divider" />
          </li>
          <li>
            <div class="dropdown-item-text fw-bold">
              {{ t('settings.company.buttons.switch_profile') }}
            </div>
          </li>
          <li>
            <div style="max-height: 30vh; overflow-y: auto">
              <div
                v-for="client in clients"
                v-show="client.id !== selectedClient?.id"
                :key="client.id"
                class="dropdown-item"
                style="cursor: pointer"
                @click.prevent="selectClient(client.id)"
              >
                {{ client?.name }}
              </div>
            </div>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { clearFulfilmentData } from '~/composables/clearFulfilmentData';
import { fetchDefaultFulfilmentData } from '~/composables/fetchDefaultFulfilmentData';
import { useClientAccess } from '~/composables/useClientAccess';
import { useModal } from '~/composables/useModal';
import { useToast } from '~/composables/useToast';
import { useClientsStore } from '~/stores/clients';
import { useInsightsStore } from '~/stores/insights';
const { t } = useI18n();
const clientsStore = useClientsStore();
const router = useRouter();
const route = useRoute();
const { hasRouteAccess } = useClientAccess();

const clients = computed(() => clientsStore.clients);

const selectedClient = computed(() => {
  return clientsStore.selectedClient;
});

const toast = useToast();
const modal = useModal();

// Add a flag to track company switching state
const isChangingCompany = ref(false);
let switchTimeoutId = null;
let modalId = null;
let removeGuard = null;
let targetRoute = null;

/**
 * Determine if a route is safe to return to after switching clients
 * Routes with dynamic parameters (client-specific data) should redirect to their parent list page
 * This dynamically detects routes with parameters instead of hardcoding patterns
 */
const getSafeRedirectRoute = newClient => {
  const currentRoute = route;
  const currentPath = currentRoute.fullPath;

  // Always redirect auth pages to home
  if (currentPath.includes('/login') || currentPath.includes('/logout')) {
    return '/';
  }

  // Check if the new client has access to the current route
  const pathWithoutQuery = currentPath.split('?')[0];

  // Check if client has access to the current route's base path
  if (!hasRouteAccess(newClient, pathWithoutQuery)) {
    // Client doesn't have access to this section - redirect to home
    return '/';
  }

  // Check if the current route has any dynamic parameters
  // Dynamic params indicate client-specific data (e.g., order ID, SKU, etc.)
  const hasParams = Object.keys(currentRoute.params).length > 0;

  if (hasParams) {
    // Route has dynamic parameters - redirect to parent path
    // Remove the last segment of the path to get the list/index page
    const pathSegments = currentPath.split('?')[0].split('/').filter(Boolean);

    if (pathSegments.length > 1) {
      // Remove the last segment (the dynamic parameter value)
      pathSegments.pop();
      return '/' + pathSegments.join('/');
    }
  }

  // For static pages and list views without params, it's safe to return to the same page
  return currentPath;
};

const selectClient = async id => {
  // Prevent switching if already in progress
  if (isChangingCompany.value) {
    toast.info(
      t('client.company_switch.in_progress_title'),
      t('client.company_switch.in_progress_message')
    );
    return;
  }

  // Hide any existing modals first
  modal.hideAllModals();

  // Find the new client to check access
  const newClient = clients.value.find(c => c.id === id);

  if (!newClient) {
    toast.error(
      t('client.company_switch.error_switch_title'),
      t('client.company_switch.error_switch_message')
    );
    return;
  }

  // Determine safe route before switching (needs to be done before route changes)
  targetRoute = getSafeRedirectRoute(newClient);

  // Capture the previous client so we can revert if any step fails partway
  // through. Without this, a failed default-data fetch would leave the cookie
  // and store pointing at the new client while the user thinks the switch
  // failed.
  const previousClientId = clientsStore.selectedClientId;

  // Set switching flag
  isChangingCompany.value = true;

  // Add navigation guard to prevent navigation during switching
  const guardCleanup = router.beforeEach((to, from, next) => {
    if (isChangingCompany.value) {
      // Block all navigation during client switching
      next(false);
    } else {
      next();
    }
  });

  // Wrap the cleanup function to also reset the reference
  removeGuard = () => {
    guardCleanup();
    removeGuard = null;
  };

  // Get company name for display
  const companyName =
    clients.value.find(c => c.id === id)?.name ||
    t('client.company_switch.generic_company_name');

  // Show company switching modal with translated content
  modalId = modal.companySwitching(companyName, {
    title: t('client.company_switch.changing_title'),
    message: t('client.company_switch.changing_message', {
      company: companyName,
    }),
  });
  // Safety timeout - ensure flags are reset if something goes wrong
  switchTimeoutId = setTimeout(() => {
    if (isChangingCompany.value) {
      isChangingCompany.value = false;

      if (modalId !== null) {
        modal.hideModal(modalId);
      }

      toast.error(
        t('client.company_switch.error_timeout_title'),
        t('client.company_switch.error_timeout_message'),
        {
          action: {
            text: t('button.common.retry'),
            onClick: () => selectClient(id),
          },
        }
      );
    }
  }, 45000); // 45 second safety timeout

  try {
    // Clear fulfilment data before switching
    await clearFulfilmentData();

    // Update modal to show that we're switching
    if (modalId !== null) {
      modal.updateModal(modalId, {
        message: t('client.company_switch.data_loading_message'),
        title: t('client.company_switch.data_loading_title'),
      });
    }

    await clientsStore.setSelectedClient(id);

    if (clientsStore.selectedClient) {
      const insightsStore = useInsightsStore();
      insightsStore.resetFilters(); // Update modal to show that we're fetching data
      if (modalId !== null) {
        modal.updateModal(modalId, {
          message: t('client.company_switch.finalize_switch_message'),
          title: t('client.company_switch.finalize_switch_title'),
        });
      }
      await Promise.all([
        fetchDefaultFulfilmentData(id, 'shops'),
        fetchDefaultFulfilmentData(id, 'shopGroups'),
        fetchDefaultFulfilmentData(id, 'shopOwners'),
        fetchDefaultFulfilmentData(id, 'dateRange'),
      ]);

      // Clean up and show success
      clearTimeout(switchTimeoutId);
      isChangingCompany.value = false;

      // Remove navigation guard
      if (removeGuard) {
        removeGuard();
      }

      if (modalId !== null) {
        modal.hideModal(modalId);
      }

      toast.success(
        t('client.company_switch.success_title'),
        t('client.company_switch.success_message', {
          company: clientsStore.selectedClient.name,
        })
      );

      // Navigate to the pre-calculated safe route
      navigateTo(targetRoute);
    } else {
      throw new Error('Selected client not found after setting');
    }
  } catch {
    // If the store/cookie was already flipped to the new client before the
    // failure (e.g. fetchDefaultFulfilmentData threw), revert to the previous
    // client so the visible state matches the error message the user is about
    // to see.
    if (
      previousClientId &&
      clientsStore.selectedClientId !== previousClientId
    ) {
      try {
        await clientsStore.setSelectedClient(previousClientId);
      } catch {
        // best-effort revert; nothing more we can do here
      }
    }

    // Always clean up properly on error
    clearTimeout(switchTimeoutId);
    isChangingCompany.value = false;

    // Remove navigation guard
    if (removeGuard) {
      removeGuard();
    }

    if (modalId !== null) {
      modal.hideModal(modalId);
    }

    toast.error(
      t('client.company_switch.error_switch_title'),
      t('client.company_switch.error_switch_message'),
      {
        action: {
          text: t('button.common.retry'),
          onClick: () => selectClient(id),
        },
      }
    );
  }
};

// Clean up on component unmount
onBeforeUnmount(() => {
  if (switchTimeoutId) {
    clearTimeout(switchTimeoutId);
  }

  // Remove navigation guard
  if (removeGuard) {
    removeGuard();
  }

  // Ensure flags are reset if component unmounts during switch
  if (isChangingCompany.value) {
    isChangingCompany.value = false;
    if (modalId !== null) {
      modal.hideModal(modalId);
    }
  }
});
</script>
